import {
  JSX,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  IOrganisationMarketingData,
  MixpanelEvents,
  OnboardingSteps,
  PolicyType,
} from "shared";
import { ArrowRightIcon, CheckIcon } from "@heroicons/react/20/solid";
import { toast } from "react-toastify";
import { When } from "react-if";

import { useGetSelectedOrganisation } from "../../../hooks/useGetSelectedOrganisation";
import { Button } from "../../../../common/Atoms/Button";
import {
  useSaveOnboardingCompleteMutation,
  useSaveOnboardingGroupPoliciesMutation,
  useSaveOnboardingSkippedMutation,
  useSaveWelcomeQuestionsMutation,
} from "../../../services/api/organisationApi/organisation";
import { mixpanelService } from "../../../../common/lib/mixpanel";
import { env } from "../../../../common/lib/env";
import { trackTrialStarted } from "../../../lib/pixels";
import { useOrganisations } from "../../../hooks/useOrganisations";
import { LoadingOverlay } from "../../Molecules/LoadingOverlay";
import { Logo } from "../../Navigation/Logo";
import { classNames } from "../../../../common/lib/classNames";

import { WelcomeQuestions } from "./WelcomeQuestions";
import { Features, OnboardingFeature } from "./Features";

const DEV_STEPS = [
  OnboardingSteps.MARKETING_DATA,
  OnboardingSteps.FEATURES,
];

/** Left panel step list. Step 3 is the welcome modal shown once onboarding completes. */
const STEP_LABELS = [
  `Tell us about your business`,
  `Choose your automations`,
  `Review your draft policies`,
];

const STEP_NUMBER: Record<OnboardingSteps, number> = {
  [OnboardingSteps.MARKETING_DATA]: 1,
  [OnboardingSteps.FEATURES]: 2,
};

export function OnboardingRoot() {
  const currentOrg = useGetSelectedOrganisation();
  const { isLoading, isFetching } = useOrganisations();
  const [ welcomeQuestions, setWelcomeQuestions ] = useState<IOrganisationMarketingData>({
    version: 1,
  });
  const [saveWelcomeQuestions, { isLoading: marketingLoading }] = useSaveWelcomeQuestionsMutation();
  const [saveOnboardingGroupsPolicies, { isLoading: groupLoading }] = useSaveOnboardingGroupPoliciesMutation();
  const [saveOnboardingComplete, { isLoading: completeLoading }] = useSaveOnboardingCompleteMutation();
  const [saveOnboardingSkipped, { isLoading: skippedLoading }] = useSaveOnboardingSkippedMutation();
  const [chosenFeatures, setChosenFeatures] = useState<OnboardingFeature[]>([
    PolicyType.REMINDER,
    PolicyType.STATEMENT,
    PolicyType.ESCALATION,
  ]);

  const [onboardingStep, setOnboardingStep] = useState<OnboardingSteps>(currentOrg?.onboarding.currentStep || OnboardingSteps.MARKETING_DATA);

  const steps: {
    [key in OnboardingSteps]: {
      title: string;
      subtitle: string;
      component: JSX.Element;
    }
  } = useMemo(() => {
    return {
      [OnboardingSteps.MARKETING_DATA]: {
        title: `Welcome to Paidnice`,
        subtitle: `We're excited to have you on board. To help us understand your needs, please answer a few questions.`,
        component: <WelcomeQuestions
          welcomeQuestions={ welcomeQuestions }
          setWelcomeQuestions={ setWelcomeQuestions }
        />,
      },
      [OnboardingSteps.FEATURES]: {
        title: `Set up your automations`,
        subtitle: `Choose the automations you'd like Paidnice to run. This will create a draft workflow that you can review, update and approve the workflow at any time.`,
        component: <Features
          chosenFeatures={ chosenFeatures }
          setChosenFeatures={ setChosenFeatures }
        />,
      },
    };
  }, [welcomeQuestions, chosenFeatures]);

  useEffect(() => {
    trackTrialStarted();
  }, []);

  useEffect(() => {
    if (env.forceOnboarding) {
      // Dev step switcher owns the current step
      return;
    }

    if (currentOrg && currentOrg.onboarding.currentStep !== onboardingStep) {
      setOnboardingStep(currentOrg.onboarding.currentStep);
    }
  }, [currentOrg, onboardingStep]);

  async function handleNext() {
    if (!currentOrg) return;

    if (onboardingStep === OnboardingSteps.MARKETING_DATA) {
      if (!welcomeQuestions.agreedToTerms) {
        toast.error(`Please agree to the Terms and Conditions to continue`);

        return;
      }

      if (!welcomeQuestions.contactNumber?.trim()) {
        toast.error(`Please provide a contact number to continue`);

        return;
      }

      saveWelcomeQuestions({
        ...welcomeQuestions,
        organisationId: currentOrg.id,
        dismissed: true, // Always mark as dismissed
      });

      return;
    }

    if (onboardingStep === OnboardingSteps.FEATURES) {
      if (chosenFeatures.length === 0) {
        toast.error(`Please select at least one feature to continue`);

        return;
      }

      try {
        // Save the selected policies and complete onboarding
        await saveOnboardingGroupsPolicies({
          organisationId: currentOrg.id,
          selectedPolicies: chosenFeatures,
        }).unwrap();

        // Complete onboarding (wait for policies to be saved first)
        onComplete();
      }
      catch (error) {
        toast.error(`Failed to save onboarding selections. Please try again.`);
      }

      return;
    }
  }

  function onComplete() {
    saveOnboardingComplete(currentOrg.id);

    mixpanelService.trackMixpanelEvent(MixpanelEvents.OnboardingCompleted);
  }

  function onSkipped() {
    saveOnboardingSkipped(currentOrg.id);

    mixpanelService.trackMixpanelEvent(MixpanelEvents.OnboardingSkipped);
  }

  let buttonText = `Continue`;

  if (onboardingStep === OnboardingSteps.FEATURES) {
    // No draft workflow unless at least one policy type is chosen
    const skipsWorkflow = !chosenFeatures.some(feature => Object.values(PolicyType).includes(feature as PolicyType));

    buttonText = skipsWorkflow ? `View Dashboard` : `Create draft workflow`;
  }

  const loading = marketingLoading || groupLoading || completeLoading || skippedLoading || isLoading || isFetching;
  const stepNumber = STEP_NUMBER[onboardingStep];

  return (
    <LoadingOverlay
      loading={ loading }
    >
      <When condition={ env.forceOnboarding }>
        <div className={ `flex items-center gap-2 mb-2 p-2 border border-dashed border-orange-500 rounded-sm` }>
          <span className={ `text-xs font-medium text-orange-600` }>{ `Dev: onboarding step` }</span>
          { DEV_STEPS.map(step => (
            <Button
              key={ step }
              size={ `sm` }
              color={ onboardingStep === step ? `orange` : `gray` }
              onClick={ () => setOnboardingStep(step) }
            >
              { step }
            </Button>
          )) }
        </div>
      </When>

      <div className={ `grid min-h-[calc(100vh-7rem)] overflow-hidden rounded-xl bg-white shadow-xl lg:grid-cols-[440px_1fr]` }>
        { /* Brand panel */ }
        <div className={ `flex flex-col bg-linear-to-br from-paidniceGreen-800 to-paidniceGreen-900 p-10 text-white lg:p-14` }>
          <div className={ `self-center` }>
            <Logo type={ `white` } />
          </div>

          <div className={ `mt-16 lg:mt-24` }>
            <div className={ `text-xs font-semibold uppercase tracking-[0.12em] text-paidniceGreen-300` }>{ `Getting started` }</div>
            <h1 className={ `mt-4 text-3xl font-bold leading-tight tracking-tight` }>{ steps[onboardingStep].title }</h1>
            <p className={ `mt-4 max-w-sm text-[15px] leading-relaxed text-paidniceGreen-200` }>{ steps[onboardingStep].subtitle }</p>
          </div>

          <ol className={ `mt-12 flex flex-col gap-5 lg:mt-16` }>
            { STEP_LABELS.map((label, index) => {
              const number = index + 1;
              const done = number < stepNumber;
              const active = number === stepNumber;

              return (
                <li
                  key={ label }
                  className={ classNames(`flex items-center gap-3.5`, !active && `opacity-55`) }
                >
                  <span className={ classNames(
                    `grid h-6.5 w-6.5 shrink-0 place-items-center rounded-full text-[13px] font-bold`,
                    done && `bg-paidniceGreen-500 text-paidniceGreen-900`,
                    active && `bg-paidniceGreen-400 text-paidniceGreen-900`,
                    !done && !active && `border-[1.5px] border-paidniceGreen-500`,
                  ) }>
                    { done ? <CheckIcon className={ `h-4 w-4` } /> : number }
                  </span>
                  <span className={ classNames(`text-[15px]`, active && `font-semibold`) }>{ label }</span>
                </li>
              );
            }) }
          </ol>

          <div className={ `mt-auto border-t border-paidniceGreen-700 pt-6 text-[13.5px] leading-relaxed text-paidniceGreen-300` }>
            { `Everything you pick becomes a draft - nothing is sent to customers until you approve it.` }
          </div>
        </div>

        { /* Step content */ }
        <div className={ `flex flex-col p-8 lg:px-20 lg:py-10` }>
          <div className={ `text-xs font-semibold uppercase tracking-[0.08em] text-paidniceGreen-500` }>{ `Step ${stepNumber} of 3` }</div>

          <div className={ onboardingStep === OnboardingSteps.MARKETING_DATA ? `mt-8 mb-8` : `mt-4 mb-6` }>
            { steps[onboardingStep].component }
          </div>

          { /* Footer */ }
          <div className={ `mt-auto flex items-center justify-between gap-4 border-t border-gray-100 pt-5` }>
            <When condition={ onboardingStep === OnboardingSteps.MARKETING_DATA }>
              <span className={ `text-[13px] text-gray-400` }>{ `Takes about a minute` }</span>
            </When>
            <When condition={ onboardingStep !== OnboardingSteps.MARKETING_DATA }>
              <a
                href={ `#` }
                onClick={ e => {
                  e.preventDefault();
                  onSkipped();
                } }
                className={ `text-[13.5px] text-gray-500 underline hover:text-gray-700` }
              >
                { `My use case is more complex, skip guided setup` }
              </a>
            </When>
            <Button
              size={ `2xl` }
              className={ `max-h-none rounded-lg` }
              onClick={ handleNext }
            >
              { buttonText }
              <ArrowRightIcon className={ `h-5 w-5 ml-2` } />
            </Button>
          </div>
        </div>
      </div>
    </LoadingOverlay>
  );
}
